/**
 * GlowTrace — a decorative animated glow border overlay.
 *
 * Renders an absolutely-positioned pseudo-element that sweeps a subtle
 * glow around the nearest `position: relative` ancestor. The parent
 * should have `overflow: hidden` so the trace stays contained.
 */

import { useEffect, useRef } from 'react';

interface GlowTraceProps {
  /** Delay in seconds before the animation starts. */
  delay?: number;
}

export function GlowTrace({ delay = 0 }: GlowTraceProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.animationDelay = `${delay}s`;
  }, [delay]);

  return (
    <>
      <div
        ref={ref}
        className="glow-trace-el pointer-events-none absolute inset-0 z-10"
        aria-hidden="true"
      />
      <style>{`
        .glow-trace-wrap {
          position: relative;
        }
        .glow-trace-el {
          opacity: 0;
          animation: glowFadeIn 1.2s ease-out forwards;
          background: linear-gradient(
            135deg,
            color-mix(in srgb, var(--vibe-color-primary) 12%, transparent) 0%,
            transparent 40%,
            transparent 60%,
            color-mix(in srgb, var(--vibe-color-primary) 8%, transparent) 100%
          );
          border-radius: inherit;
        }
        @keyframes glowFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </>
  );
}

export default GlowTrace;
